import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";

const EASE = [0.16, 1, 0.3, 1];
const PALETTE = ["#ED7120", "#D62F34", "#8157FF", "#FAC222", "#00AA4F", "#00A1DD", "#EE427D"];

const prefersReduced =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ------------------------------- Primitives ------------------------------- */

const MaskLine = ({ children, delay = 0, className = "", once = true, testId }) => (
  <motion.span
    className="mask-line"
    data-testid={testId}
    initial="hidden"
    whileInView="show"
    viewport={{ once, margin: "-8% 0px" }}
  >
    <motion.span
      className={className}
      style={{ display: "block", willChange: "transform" }}
      variants={{
        hidden: { y: prefersReduced ? "0%" : "110%" },
        show: { y: "0%", transition: { duration: 1.05, ease: EASE, delay } },
      }}
    >
      {children}
    </motion.span>
  </motion.span>
);

const FadeUp = ({ children, delay = 0, className = "", testId }) => (
  <motion.div
    className={className}
    data-testid={testId}
    initial={prefersReduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-10% 0px" }}
    transition={{ duration: 0.9, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

const FollowButton = ({ fill = "#ED7120", label = "Follow the journey", testId = "follow-button" }) => (
  <a
    href="https://www.instagram.com/alchemyofeducation/"
    target="_blank"
    rel="noopener noreferrer"
    className="alc-btn"
    style={{ "--alc-fill": fill }}
    data-testid={testId}
    aria-label={`${label} on Instagram, @alchemyofeducation`}
  >
    <span>{label}</span>
    <span className="arrow" aria-hidden="true">&#8594;</span>
  </a>
);

// Kinetic per-letter wordmark. Each letter reveals an Alchemy colour on hover.
const KineticWordmark = ({ style, className = "", testId }) => {
  const words = ["ALCHEMY", "OF", "EDUCATION"];
  let counter = 0;
  return (
    <span
      className={`display leading-[0.9] ${className}`}
      style={style}
      data-testid={testId}
      aria-label="Alchemy of Education"
    >
      {words.map((word, wi) => (
        <span
          key={wi}
          className="inline-block whitespace-nowrap"
          style={{ marginRight: "0.3em" }}
        >
          {word.split("").map((ch, ci) => {
            const color = PALETTE[counter % PALETTE.length];
            counter += 1;
            return (
              <span key={ci} className="kletter" style={{ "--kc": color }} aria-hidden="true">
                {ch}
              </span>
            );
          })}
        </span>
      ))}
    </span>
  );
};

const Kicker = ({ children, dot, testId }) => (
  <FadeUp>
    <p className="micro-label text-[#111]" data-testid={testId}>
      {dot && (
        <span
          className="inline-block w-2 h-2 rounded-full align-middle mr-3"
          style={{ background: dot }}
        />
      )}
      {children}
    </p>
  </FadeUp>
);

// "Coming Soon" — bigger, with a slow travelling colour shimmer + per-letter hover.
const ComingSoonKinetic = () => {
  const text = "Coming Soon";
  let ci = 0;
  return (
    <div
      className="display text-[#111] leading-[0.95]"
      style={{ fontSize: "clamp(30px, 5.4vw, 84px)", letterSpacing: "-0.025em" }}
      data-testid="hero-label"
      aria-label="Coming Soon"
    >
      {text.split("").map((ch, i) => {
        if (ch === " ") {
          return (
            <span key={i} style={{ display: "inline-block", width: "0.32em" }} aria-hidden="true" />
          );
        }
        const color = PALETTE[ci % PALETTE.length];
        ci += 1;
        const order = ci;
        return (
          <motion.span
            key={i}
            aria-hidden="true"
            className="inline-block"
            style={{ willChange: "color, transform" }}
            initial={{ color: "#111111" }}
            animate={prefersReduced ? { color: "#111111" } : { color: ["#111111", color, "#111111"] }}
            transition={
              prefersReduced
                ? {}
                : { duration: 2.6, ease: "easeInOut", repeat: Infinity, repeatDelay: 2.6, delay: order * 0.13 }
            }
            whileHover={{ color, y: -6 }}
          >
            {ch}
          </motion.span>
        );
      })}
    </div>
  );
};

/* -------------------------------- Sections -------------------------------- */

const Masthead = () => (
  <header
    className="mx-auto w-full max-w-[1440px] px-6 md:px-12 pt-8 md:pt-12 pb-8 md:pb-14"
    data-testid="site-wordmark"
  >
    <motion.div
      initial={prefersReduced ? { opacity: 1, y: 0 } : { opacity: 0, y: -14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: EASE, delay: 0.1 }}
    >
      <KineticWordmark
        style={{ fontSize: "clamp(30px, 6.4vw, 92px)" }}
        testId="masthead-wordmark"
      />
    </motion.div>
  </header>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-[86svh] md:min-h-[90svh] flex flex-col justify-center"
      data-testid="section-hero"
    >
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12 py-14 md:py-10">
        <motion.div
          className="mb-9 md:mb-14 pt-6 md:pt-8 border-t border-neutral-200"
          initial={prefersReduced ? { opacity: 1 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 0.35 }}
        >
          <ComingSoonKinetic />
        </motion.div>

        <motion.h1
          className="display text-[#111]"
          style={{ fontSize: "clamp(44px, 9vw, 132px)", opacity, lineHeight: 0.94 }}
        >
          <motion.span style={{ y: prefersReduced ? 0 : y1, display: "block" }}>
            <MaskLine delay={0.4} testId="hero-line-1">
              Understand <span style={{ color: "#8157FF" }}>yourself.</span>
            </MaskLine>
            <MaskLine delay={0.52}>
              Find your <span style={{ color: "#ED7120" }}>direction.</span>
            </MaskLine>
            <MaskLine delay={0.64}>
              Build what comes <span style={{ color: "#EE427D" }}>next.</span>
            </MaskLine>
          </motion.span>
        </motion.h1>

        <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-8 items-end">
          <FadeUp delay={0.9} className="md:col-span-7 lg:col-span-6 md:col-start-6 lg:col-start-7">
            <p className="body-copy text-neutral-700 max-w-xl">
              A new way to explore who you are, what drives you, and where you could go —
              through <span className="text-[#111] font-medium">psychology</span>,{" "}
              <span className="text-[#111] font-medium">philosophy</span>, and a deeper
              understanding of yourself.
            </p>
            <div className="mt-8">
              <FollowButton fill="#ED7120" testId="hero-follow-button" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => {
  const items = ["Understand yourself", "Find your direction", "Build what comes next", "Less noise, more understanding"];
  const line = items.join("   \u2022   ") + "   \u2022   ";
  return (
    <section
      className="py-8 md:py-14 border-y border-neutral-200 overflow-hidden select-none"
      data-testid="section-marquee"
      aria-hidden="true"
    >
      <div className="marquee-track">
        {[0, 1].map((k) => (
          <span key={k} className="display text-neutral-200 pr-6" style={{ fontSize: "clamp(34px, 6vw, 104px)" }}>
            {line}
          </span>
        ))}
      </div>
    </section>
  );
};

const CoreThought = () => (
  <section className="py-20 md:py-[18vh]" data-testid="section-core-thought">
    <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12">
      <div className="max-w-[1000px]">
        <h2 className="display text-[#111]" style={{ fontSize: "clamp(30px, 5vw, 84px)", lineHeight: 1.0 }}>
          <MaskLine>The world gives you</MaskLine>
          <MaskLine delay={0.06}>plenty of answers.</MaskLine>
          <MaskLine delay={0.12} className="text-neutral-400">
            Very few teach you how
          </MaskLine>
          <MaskLine delay={0.18} className="text-neutral-400">
            to find <span style={{ color: "#00A1DD" }}>your own.</span>
          </MaskLine>
        </h2>
      </div>

      <div className="mt-14 md:mt-20 grid md:grid-cols-12 gap-8">
        <FadeUp className="md:col-span-6 lg:col-span-5">
          <p className="body-copy text-neutral-700">
            There is more to discovering your future than choosing a career. It begins with
            understanding the person making the choice.
          </p>
        </FadeUp>
        <div className="md:col-span-6 lg:col-start-8">
          <ul className="flex flex-wrap gap-x-6 gap-y-2" data-testid="core-word-list">
            {["Your patterns.", "Your strengths.", "Your interests.", "Your values.", "Your ambitions.", "Your contradictions."].map((w, i) => (
              <li key={i}>
                <FadeUp delay={i * 0.05}>
                  <span className="display" style={{ fontSize: "clamp(22px, 3vw, 40px)", color: PALETTE[i % PALETTE.length] }}>
                    {w}
                  </span>
                </FadeUp>
              </li>
            ))}
          </ul>
          <FadeUp delay={0.2}>
            <p className="body-copy text-[#111] font-medium mt-8">
              Alchemy exists to help you make sense of them.
            </p>
          </FadeUp>
        </div>
      </div>
    </div>
  </section>
);

const FLOW = [
  { label: "Questions", color: "#ED7120" },
  { label: "Reflection", color: "#EE427D" },
  { label: "Understanding", color: "#8157FF" },
  { label: "Clarity", color: "#00A1DD" },
  { label: "Direction", color: "#00AA4F" },
];

const TheAlchemy = () => (
  <section className="py-20 md:py-[18vh]" data-testid="section-the-alchemy">
    <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12">
      <Kicker dot="#8157FF" testId="alchemy-kicker">The Alchemy</Kicker>

      <h2 className="display text-[#111] mt-8 md:mt-12" style={{ fontSize: "clamp(32px, 5vw, 88px)", lineHeight: 1.0 }}>
        <MaskLine>Turn introspection</MaskLine>
        <MaskLine delay={0.08}>into <span style={{ color: "#ED7120" }}>direction.</span></MaskLine>
      </h2>

      <div className="mt-12 grid md:grid-cols-2 gap-8 md:gap-16 max-w-4xl">
        <FadeUp>
          <p className="body-copy text-neutral-700">
            Through carefully designed psychological and philosophical assessments, expert
            analysis, and structured reflection, Alchemy helps you look inward — and make sense
            of what you find.
          </p>
        </FadeUp>
        <FadeUp delay={0.12}>
          <p className="body-copy text-neutral-700">
            Not to tell you who to become.
            <br />
            <span className="text-[#111] font-medium">To help you understand who you already are.</span>
          </p>
        </FadeUp>
      </div>

      <div className="mt-16 md:mt-24" data-testid="how-it-starts-flow">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-3 md:gap-x-6">
          {FLOW.map((step, i) => (
            <FadeUp key={i} delay={i * 0.08} className="flex items-center gap-x-3 md:gap-x-6">
              <span className="display" style={{ fontSize: "clamp(24px, 3.6vw, 60px)", color: step.color }}>
                {step.label}
              </span>
              {i < FLOW.length - 1 && (
                <span className="text-neutral-300" style={{ fontSize: "clamp(20px, 2.6vw, 44px)" }} aria-hidden="true">
                  &#8594;
                </span>
              )}
            </FadeUp>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const LEADS = [
  "Psychometric assessment.",
  "Personality insights.",
  "Career discovery.",
  "Expert analysis.",
  "Personal roadmaps.",
  "Manifestos for the way forward.",
  "Learning experiences.",
  "A community of people figuring it out, too.",
];

const WhatItLeadsTo = () => (
  <section className="py-20 md:py-[16vh]" data-testid="section-leads-to">
    <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12">
      <h2 className="display text-[#111]" style={{ fontSize: "clamp(30px, 4.6vw, 80px)", lineHeight: 1.0 }}>
        <MaskLine><span style={{ color: "#00AA4F" }}>Know yourself.</span></MaskLine>
        <MaskLine delay={0.07}><span style={{ color: "#00A1DD" }}>See your possibilities.</span></MaskLine>
        <MaskLine delay={0.14}><span style={{ color: "#8157FF" }}>Choose your direction.</span></MaskLine>
      </h2>

      <FadeUp className="mt-12 md:mt-16">
        <p className="micro-label text-neutral-500">Alchemy will bring together</p>
      </FadeUp>

      <ul className="mt-6" data-testid="leads-list">
        {LEADS.map((item, i) => (
          <li key={i} className="border-t border-neutral-200 py-5 md:py-7" data-testid={`lead-${i}`}>
            <div className="flex items-baseline gap-4 md:gap-8">
              <span className="micro-label shrink-0" style={{ color: PALETTE[i % PALETTE.length] }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="display text-[#111]" style={{ fontSize: "clamp(24px, 3.8vw, 58px)", lineHeight: 1.02 }}>
                <MaskLine>{item}</MaskLine>
              </h3>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-16 md:mt-24 max-w-[1000px]">
        <h3 className="display" style={{ fontSize: "clamp(30px, 5.2vw, 96px)", lineHeight: 0.98 }}>
          <MaskLine>What could you do</MaskLine>
          <MaskLine delay={0.08}>with the <span style={{ color: "#ED7120" }}>person you are?</span></MaskLine>
        </h3>
      </div>
    </div>
  </section>
);

const Philosophy = () => (
  <section className="py-20 md:py-[18vh]" data-testid="section-philosophy">
    <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12">
      <Kicker dot="#D62F34" testId="philosophy-kicker">The Philosophy</Kicker>

      <h2 className="display text-[#111] mt-8 md:mt-12" style={{ fontSize: "clamp(34px, 6vw, 104px)", lineHeight: 0.96 }}>
        <MaskLine>You don&apos;t need</MaskLine>
        <MaskLine delay={0.07} className="text-neutral-400">more noise.</MaskLine>
        <MaskLine delay={0.14}>You need <span style={{ color: "#00AA4F" }}>clarity.</span></MaskLine>
      </h2>

      <div className="mt-12 md:mt-16 grid md:grid-cols-12 gap-8">
        <FadeUp className="md:col-span-6 lg:col-span-5">
          <p className="body-copy text-neutral-700">
            A world of endless choices can make direction feel impossible. More careers. More
            opinions. More content. More people telling you what success should look like.
          </p>
        </FadeUp>
        <FadeUp delay={0.12} className="md:col-span-6 lg:col-start-8">
          <p className="body-copy text-[#111] font-medium">
            Alchemy takes a different approach.
            <br />
            <span style={{ color: "#8157FF" }}>Less noise. More understanding.</span>
          </p>
        </FadeUp>
      </div>
    </div>
  </section>
);

const Closing = () => (
  <section className="py-24 md:py-[20vh]" data-testid="section-closing">
    <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12">
      <h2 className="display text-[#111] max-w-[1100px]" style={{ fontSize: "clamp(40px, 8vw, 150px)", lineHeight: 0.92 }}>
        <MaskLine>There is a version</MaskLine>
        <MaskLine delay={0.08}>of your life you</MaskLine>
        <MaskLine delay={0.16}>haven&apos;t <span style={{ color: "#8157FF" }}>discovered</span> yet.</MaskLine>
      </h2>

      <FadeUp delay={0.2} className="mt-10 md:mt-14 max-w-2xl">
        <p className="body-copy text-neutral-700">
          We&apos;re building the tools to help you find it.
          <br />
          <span className="text-[#111] font-medium">Alchemy of Education is coming soon.</span>
        </p>
      </FadeUp>

      <FadeUp delay={0.3} className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
        <FollowButton fill="#8157FF" testId="closing-follow-button" />
        <a
          href="https://www.instagram.com/alchemyofeducation/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-link body-copy font-medium"
          data-testid="closing-handle-link"
        >
          @alchemyofeducation
        </a>
      </FadeUp>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-neutral-200 pt-14 pb-12 md:pt-20 md:pb-16" data-testid="site-footer">
    <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12">
      <KineticWordmark
        style={{ fontSize: "clamp(34px, 8vw, 128px)" }}
        testId="footer-wordmark"
      />
      <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <span className="micro-label text-neutral-400">
          © 2026 Alchemy of Education. All rights reserved.
        </span>
        <a
          href="https://www.instagram.com/alchemyofeducation/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-link micro-label"
          data-testid="footer-handle-link"
        >
          @alchemyofeducation
        </a>
      </div>
    </div>
  </footer>
);

/* ---------------------------------- Page ---------------------------------- */

export default function ComingSoon() {
  const [, setReady] = useState(false);

  useEffect(() => {
    if (prefersReduced) {
      setReady(true);
      return;
    }
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.scrollTo(0, 0);
    setReady(true);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <main className="bg-white text-[#111] antialiased overflow-x-hidden" data-testid="coming-soon-page">
      <Masthead />
      <Hero />
      <Marquee />
      <CoreThought />
      <TheAlchemy />
      <WhatItLeadsTo />
      <Philosophy />
      <Closing />
      <Footer />
    </main>
  );
}
